// @ts-nocheck
import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as FullscreenIcon } from '../../../../assets/Icons/lucideIcons/maximize.svg';
import { ReactComponent as FullscreenExitIcon } from '../../../../assets/Icons/lucideIcons/minimize.svg';

import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import {
  useDeleteDocumentMutation,
  useDownloadDocumentMutation,
} from '../../../../features/document/api/documentInventoryQueries';
import { useDialogFullscreen } from '../../../../hooks/useDialogFullscreen';

// Import specific field components for modal data types
import {
  ChecklistFieldComponent,
  DocumentFieldComponent,
  MultiDocumentFieldComponent,
  TableFieldComponent,
  IframeFieldComponent,
  TextFieldExtendedComponent,
  AdvancedTableRenderer,
  ParagraphFieldRenderer,
  CanvasFieldRenderer,
} from '../FieldComponents';
import {
  checkAttributeIsRequired,
  downloadBlobResponse,
  isValueEmpty,
} from '../../../../utils/Utils';
import { useUploadDocumentContext } from '../../../../context/DocumentUpload/UploadDocumentContext';
import { DocumentCleanupProvider } from '../../../../context/DocumentCleanupContext';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import swalToast from '../../swalToast';

/**
 * Modal component for editing complex attribute values in multi-edit mode
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.handleClose - Function to close the modal
 * @param {Object} props.attribute - The attribute being edited
 * @param {any} props.value - The current value of the attribute
 * @param {Function} props.handleSave - Function to save the new value
 * @param {Array} props.associationList - List of available associations (for Association type)
 * @param {Object} props.entityData - Entity data for document fields
 * @returns {React.ReactElement} The MultiEditModal component
 */
const MultiEditModal = ({
  open,
  handleClose,
  attribute,
  value,
  handleSave,
  entityData = EMPTY_OBJECT,
  entityDetailsFormikForm,
  handleAutoSave,
  isDerivedExtended = false,
  usersList = EMPTY_ARRAY,
  configColumn = false,
  modelTemplateDetails = EMPTY_OBJECT,
  isUserAuthorizedToOverrideAttribute = false,
  mode = 'edit',
}) => {
  const initialValueRef = useRef(null);
  const hasInitialSnapshotRef = useRef(false);
  const extendedFieldRef = useRef(null);
  const canvasFieldRef = useRef(null);
  const advancedTableRef = useRef(null);
  const cleanupRef = useRef(null);
  // Tracks value changes within the modal session so removals reflect immediately
  const [currentValue, setCurrentValue] = useState(value);
  const [failedDocsConfirmOpen, setFailedDocsConfirmOpen] = useState(false);
  const [failedDocsList, setFailedDocsList] = useState([]);

  const { updateFileAttributes } = useAttribute();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();
  const { mutateAsync: downloadDocument } = useDownloadDocumentMutation();
  const {
    handleStopUpload,
    activeBatches,
    uploadErrors = EMPTY_OBJECT,
    clearUploadError,
  } = useUploadDocumentContext();
  const { isFullScreen, toggleFullScreen, exitFullScreen } =
    useDialogFullscreen();

  const isRequired = useMemo(
    () => checkAttributeIsRequired(attribute),
    [attribute]
  );
  /**
   * Handles value change from field components
   * @function
   * @param {any} newValue - The new value
   * @returns {void}
   */
  const handleValueChange = useCallback(
    (newValue) => {
      const isDocType = attribute?.data_type === 'Document';
      // For document attributes, we only update Formik/Draft state if:
      // 1. the value is being cleared (to properly reflect emptiness)
      // 2. OR it already contains a document_id (meaning it was fully uploaded)
      const shouldUpdateState =
        !isDocType ||
        isValueEmpty(newValue) ||
        (Array.isArray(newValue) &&
          newValue?.length > 0 &&
          (newValue[0]?.document_id || newValue[0]?.document_name));

      if (shouldUpdateState) {
        setCurrentValue(newValue);
        handleSave(newValue);
      }

      if (attribute?.id && handleAutoSave && shouldUpdateState) {
        handleAutoSave(attribute.id, newValue, {
          persist: false,
          debounce: false,
        });
      }
    },
    [attribute, handleAutoSave, handleSave, setCurrentValue]
  );

  /**
   * Handles modal cancellation by identifying and deleting any newly uploaded documents
   * and subsequently reverting form state to its initial value.
   * @function
   * @returns {void}
   */
  const handleCancelChanges = useCallback(async () => {
    exitFullScreen();
    advancedTableRef.current?.discard?.();
    if (attribute?.input_control_type === 'MULTI_DOCUMENT') {
      await cleanupRef.current?.cleanupAllDocuments?.();
    } else if (attribute?.data_type === 'Document') {
      // Stop any in-progress uploads for this attribute
      activeBatches.forEach((batch) => {
        (batch.data || []).forEach((f) => {
          if (f.attribute_id === attribute?.id && f.key) {
            handleStopUpload(f.key);
          }
        });
      });

      const initialIDs = new Set(
        (Array.isArray(initialValueRef.current) ? initialValueRef.current : [])
          .map((doc) => doc?.document_id)
          .filter(Boolean)
          .map(String)
      );
      let currentDocs = [];
      if (Array.isArray(currentValue)) {
        currentDocs = currentValue;
      } else if (currentValue) {
        currentDocs = [currentValue];
      } else if (Array.isArray(value)) {
        currentDocs = value;
      }
      const currentIDs = Array.from(
        new Set(
          currentDocs
            .map((doc) => doc?.document_id)
            .filter(Boolean)
            .map(String)
        )
      );

      currentIDs.forEach((id) => {
        if (!initialIDs.has(id)) {
          deleteDocument({ document_id: id });
        }
      });
    }

    if (attribute?.data_type === 'Document') {
      if (attribute?.id && handleAutoSave) {
        handleAutoSave(attribute.id, initialValueRef.current, {
          persist: true,
        });
      }
    }

    const isDocType = attribute?.data_type === 'Document';
    const restoredValue = initialValueRef.current ?? (isDocType ? [] : '');
    setCurrentValue(restoredValue);
    handleSave(restoredValue);
    if (attribute?.id && handleAutoSave && isDocType) {
      handleAutoSave(attribute.id, restoredValue, {
        persist: false,
        debounce: false,
      });
    }
    handleClose();
  }, [
    exitFullScreen,
    handleClose,
    handleValueChange,
    attribute,
    currentValue,
    value,
    deleteDocument,
    handleStopUpload,
    activeBatches,
    entityData,
    handleAutoSave,
  ]);

  const existingDocument = useMemo(() => {
    if (
      attribute?.data_type !== 'Document' ||
      attribute?.input_control_type === 'MULTI_DOCUMENT'
    ) {
      return null;
    }
    const doc = Array.isArray(currentValue) ? currentValue[0] : currentValue;
    return doc?.document_id ? doc : null;
  }, [attribute?.data_type, attribute?.input_control_type, currentValue]);

  /**
   * Downloads the currently uploaded document for this attribute.
   * @function
   * @returns {void}
   */
  const handleDownloadDocument = useCallback(() => {
    if (!existingDocument) return;
    const docId = existingDocument.document_id;
    if (!docId) return;

    let exportType =
      existingDocument.document_type || existingDocument.file_type || '';

    if (!exportType && existingDocument.document_name?.includes('.')) {
      exportType = existingDocument.document_name.split('.').pop();
    } else if (!exportType && existingDocument.file_name?.includes('.')) {
      exportType = existingDocument.file_name.split('.').pop();
    }

    downloadDocument({
      params: {
        document_id: docId,
        entity_id: entityData?.entity_id,
        entity_type: entityData?.entity_type,
        export_type: exportType,
        commit_id: 1,
      },
      config: {
        responseType: exportType === 'onedrive_docx' ? 'json' : 'blob',
        loader: false,
      },
    })
      .then((response) => {
        if (!response) return;
        if (exportType === 'onedrive_docx') {
          window.open(response?.path, '_blank');
          return;
        }
        downloadBlobResponse(
          response,
          `${existingDocument.document_name}.${exportType}`
        );
      })
      .catch((e) => {
        swalToast({
          icon: 'error',
          title: `Error occurred at time of downloading file. ${e?.message || e || ''}`,
        });
      });
  }, [existingDocument, downloadDocument, entityData]);

  /**
   * Below handleOnClose specially added for document type attr as we need to save tags
   * for that doc once user click on save button.
   * If attr is other than doc type just call handleClose.
   */

  const persistDraftOnSave = useCallback(
    (committedValue) => {
      if (attribute?.input_control_type === 'TEXTFIELD_EXTENDED') {
        extendedFieldRef.current?.flush?.();
      }
      if (attribute?.input_control_type === 'Canvas') {
        canvasFieldRef.current?.flush?.();
      }
      if (attribute?.id && handleAutoSave) {
        const attrId = attribute.id;
        const latest =
          committedValue ??
          entityDetailsFormikForm?.values?.[attrId] ??
          entityDetailsFormikForm?.values?.[Number(attrId)] ??
          currentValue;
        handleAutoSave(attrId, latest, { persist: true });
      }
    },
    [
      attribute?.id,
      attribute?.input_control_type,
      handleAutoSave,
      entityDetailsFormikForm?.values,
      currentValue,
    ]
  );

  const executeSave = useCallback(
    async (valueToSave) => {
      const isDocType = attribute?.data_type === 'Document';
      if (isDocType) {
        // Removing a document from an attribute does NOT use the Document Inventory
        // DELETE endpoint. The remaining docs are sent via updateFileAttributes and
        // the draft is persisted without the removed doc — no DELETE call needed.
        let currentDocs = [];
        if (Array.isArray(valueToSave)) {
          currentDocs = valueToSave;
        } else if (valueToSave) {
          currentDocs = [valueToSave];
        }

        if (!isValueEmpty(valueToSave)) {
          const { entity_id, entity_type } = entityData || {};

          const payload = currentDocs
            .filter((doc) => doc?.document_id)
            .map((doc) => ({
              entity_id,
              entity_type,
              document_id: doc.document_id,
              tags: doc.tags || [],
            }));

          if (payload.length > 0) {
            try {
              await updateFileAttributes(payload);
            } catch (err) {
              console.error('Failed to update document attributes', err);
              swalToast({
                icon: 'error',
                title: 'Failed to update document tags. Please try again.',
              });
              return;
            }
          }
        }
      }

      const committedValue = advancedTableRef.current?.commit?.();

      // Sanitize document values before persisting to draft — strip transient UI
      // fields (key, status, error, file) so the draft only contains clean data.
      let sanitizedValue = committedValue ?? valueToSave;
      if (isDocType && Array.isArray(sanitizedValue)) {
        sanitizedValue = sanitizedValue
          .filter(
            (doc) =>
              doc &&
              typeof doc === 'object' &&
              doc.document_id != null &&
              !String(doc.document_id).startsWith('doc-') &&
              doc.document_id !== ''
          )
          .map((doc) => ({
            document_id: doc.document_id,
            document_name: doc.document_name || doc.file_name || '',
            document_type: doc.document_type || doc.file_type || '',
            tags: Array.isArray(doc.tags) ? doc.tags : [],
          }));
      }

      persistDraftOnSave(sanitizedValue);
      handleClose();
    },
    [
      attribute?.data_type,
      attribute?.input_control_type,
      entityData,
      updateFileAttributes,
      persistDraftOnSave,
      handleClose,
    ]
  );

  const handleOnClose = useCallback(async () => {
    const isDocType = attribute?.data_type === 'Document';
    if (isDocType && !isValueEmpty(currentValue)) {
      const docs = Array.isArray(currentValue) ? currentValue : [currentValue];
      // Check if any documents failed to upload
      const failedDocs = docs.filter(
        (doc) =>
          Boolean(doc?.error) || Boolean(doc?.key && uploadErrors?.[doc?.key])
      );
      if (failedDocs.length > 0) {
        setFailedDocsList(failedDocs);
        setFailedDocsConfirmOpen(true);
        return;
      }
    }

    await executeSave(currentValue);
  }, [
    attribute?.data_type,
    currentValue,
    uploadErrors,
    executeSave,
    setFailedDocsList,
    setFailedDocsConfirmOpen,
  ]);

  const handleCancelFailedConfirm = useCallback(() => {
    setFailedDocsConfirmOpen(false);
  }, [setFailedDocsConfirmOpen]);

  const handleRemoveFailedAndSave = useCallback(async () => {
    const docs = Array.isArray(currentValue) ? currentValue : [currentValue];
    const validDocs = docs.filter(
      (doc) => !doc?.error && !(doc?.key && uploadErrors?.[doc?.key])
    );

    // Clear background upload errors for removed failed documents
    failedDocsList.forEach((doc) => {
      if (doc?.key) {
        clearUploadError?.(doc.key);
      }
    });

    handleValueChange(validDocs);
    setFailedDocsConfirmOpen(false);
    await executeSave(validDocs);
  }, [
    currentValue,
    uploadErrors,
    failedDocsList,
    clearUploadError,
    handleValueChange,
    executeSave,
    setFailedDocsConfirmOpen,
  ]);
  // store the initial value when this model get opened
  useEffect(() => {
    // if not open don't store value inside ref
    if (!open) {
      hasInitialSnapshotRef.current = false;
      initialValueRef.current = null;
      return;
    }

    // take snapshot of old value only if not already taken
    if (!hasInitialSnapshotRef.current) {
      initialValueRef.current = Array.isArray(value)
        ? value.map((doc) =>
            typeof doc === 'object' && doc !== null ? { ...doc } : doc
          )
        : value;
      hasInitialSnapshotRef.current = true;
      setCurrentValue(value);
    }
  }, [open, value, setCurrentValue]);

  /**
   * Synchronizes incoming document values (from background upload completions or Formik)
   * into the modal's local state (`currentValue`) while the modal is open.
   *
   * When background uploads complete via SSE, `useUploadDocumentInBackground` writes newly
   * assigned `document_id`s directly to Formik. This effect merges those updated IDs into
   * `currentValue` while preserving any local tag selections or name edits made by the user,
   * supporting both single-document and multi-document attributes.
   */
  useEffect(() => {
    if (!open || attribute?.data_type !== 'Document') return;
    const isMultiDoc = attribute?.input_control_type === 'MULTI_DOCUMENT';

    if (isMultiDoc) {
      if (!Array.isArray(value) || value.length === 0) return;
      setCurrentValue((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        return value.map((incomingDoc) => {
          const localMatch = prevArray.find(
            (p) =>
              (p.document_id && p.document_id === incomingDoc.document_id) ||
              (p.key && p.key === incomingDoc.key) ||
              (p.document_name && p.document_name === incomingDoc.document_name)
          );
          return {
            ...incomingDoc,
            status: incomingDoc.document_id
              ? 'Verified'
              : (localMatch?.status ?? incomingDoc.status),
            tags: localMatch?.tags ?? incomingDoc.tags,
            document_name:
              localMatch?.document_name ?? incomingDoc.document_name,
          };
        });
      });
    } else {
      const incomingDoc = Array.isArray(value) ? value[0] : value;
      if (!incomingDoc?.document_id) return;
      setCurrentValue((prev) => {
        const currentDoc = Array.isArray(prev) ? prev[0] : prev;
        if (currentDoc?.document_id === incomingDoc.document_id) return prev;
        return [{ ...incomingDoc, tags: currentDoc?.tags ?? incomingDoc.tags }];
      });
    }
  }, [
    value,
    open,
    attribute?.data_type,
    attribute?.input_control_type,
    setCurrentValue,
  ]);

  /**
   * Get modal size based on data type and input type
   * @returns {string} The modal size
   */
  const getModalSize = useCallback(() => {
    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;

    switch (inputType) {
      case 'TEXTFIELD_EXTENDED':
        return 'lg';
      case 'Canvas':
        return 'lg';
      default:
        break;
    }

    switch (dataType) {
      case 'Table':
        return 'md';
      case 'AdvancedTable':
        return 'xl';
      case 'Chart':
        return 'md';
      case 'Checklist':
        return 'lg';
      case 'Document':
        if (attribute?.input_control_type === 'MULTI_DOCUMENT') {
          return 'lg';
        }
        return 'sm';
      case 'Paragraph':
        return 'lg';
      case 'FieldChange':
        return 'xl';
      default:
        return 'sm';
    }
  }, [attribute?.data_type, attribute?.input_control_type]);

  const dialogMaxWidth = isFullScreen ? false : getModalSize();

  /**
   * Renders the appropriate field component based on attribute data type
   * @function
   * @returns {React.ReactElement} The rendered field component
   */
  const renderFieldComponent = useCallback(() => {
    if (!attribute) return null;

    const isLongLabel = attribute?.label?.length > 90;

    const fieldProps = {
      label: isLongLabel
        ? attribute?.label?.slice(0, 90) + '...'
        : attribute?.label,
      titleLabel: attribute?.label,
      value: currentValue,
      onChange: handleValueChange,
      attribute,
      entityDetailsFormikForm,
      required: isRequired, // Add required prop
      configColumn,
      modelTemplateDetails,
      mode,
    };

    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;

    switch (inputType) {
      case 'Canvas':
        return <CanvasFieldRenderer ref={canvasFieldRef} {...fieldProps} />;
      case 'TEXTFIELD_EXTENDED':
        return (
          <TextFieldExtendedComponent ref={extendedFieldRef} {...fieldProps} />
        );
      case 'IFRAME':
        return <IframeFieldComponent {...fieldProps} />;
      default:
        break;
    }

    switch (dataType) {
      case 'Table':
        return <TableFieldComponent {...fieldProps} />;

      case 'AdvancedTable':
        return (
          <AdvancedTableRenderer
            ref={advancedTableRef}
            {...fieldProps}
            entityData={entityData}
            usersList={usersList}
            isViewMode={mode === 'view'}
            isUserAuthorizedToOverrideAttribute={
              isUserAuthorizedToOverrideAttribute
            }
          />
        );

      case 'Chart':
        return <IframeFieldComponent {...fieldProps} />;

      case 'Checklist':
        return (
          <ChecklistFieldComponent
            {...fieldProps}
            entityId={entityData?.entity_id}
            entityType={entityData?.entity_type}
          />
        );

      case 'Document':
        if (attribute?.input_control_type === 'MULTI_DOCUMENT') {
          return (
            <MultiDocumentFieldComponent
              {...fieldProps}
              entityData={entityData}
              configColumn={configColumn}
            />
          );
        }
        return (
          <DocumentFieldComponent {...fieldProps} entityData={entityData} />
        );
      case 'Paragraph':
        return <ParagraphFieldRenderer {...fieldProps} />;
      default:
        return null;
    }
  }, [
    attribute,
    handleValueChange,
    entityData,
    entityDetailsFormikForm,
    isRequired,
    configColumn,
    modelTemplateDetails,
    usersList,
    currentValue,
    isDerivedExtended,
    isUserAuthorizedToOverrideAttribute,
    mode,
  ]);

  /**
   * Get modal title based on data type and input type
   * @returns {string} The modal title
   */
  const getModalTitle = useCallback(() => {
    const dataType = attribute?.data_type;
    const inputType = attribute?.input_control_type;
    const label = attribute?.label || attribute?.name;

    switch (inputType) {
      case 'TEXTFIELD_EXTENDED':
        return isDerivedExtended ? `View` : `Edit`;

      default:
        break;
    }

    switch (dataType) {
      case 'Table':
        return `Edit Table - ${label}`;
      case 'AdvancedTable':
        return `Edit Advanced Table - ${label}`;
      case 'Chart':
        return `Edit Chart - ${label}`;
      case 'Checklist':
        return `Edit Checklist - ${label}`;
      case 'Document':
        return `Edit Document - ${label}`;
      case 'Paragraph':
        return `Edit Paragraph - ${label}`;
      default:
        return `Edit ${label}`;
    }
  }, [
    attribute?.data_type,
    attribute?.input_control_type,
    attribute?.label,
    attribute?.name,
  ]);

  const saveButtonText =
    attribute?.data_type === 'Table' && !currentValue?.data?.length
      ? 'Save'
      : 'Save Changes';
  const saveButtonTitle = isDerivedExtended
    ? 'This action is not supported for derived attributes.'
    : saveButtonText;

  const handleDialogClose = useCallback(
    (_event, _reason) => {
      if (mode === 'view') {
        handleClose();
      } else {
        handleCancelChanges();
      }
    },
    [mode, handleClose, handleCancelChanges]
  );

  return (
    <DocumentCleanupProvider cleanupRef={cleanupRef}>
      <Dialog
        open={open && !failedDocsConfirmOpen}
        onClose={handleDialogClose}
        fullScreen={isFullScreen}
        aria-labelledby="scroll-dialog-title"
        maxWidth={dialogMaxWidth}
        aria-describedby="scroll-dialog-description"
        fullWidth
        slotProps={{
          transition: {
            onExited: () => exitFullScreen(),
          },
        }}
        PaperProps={{
          sx: {
            minHeight: isFullScreen ? '100vh' : '550px',
            maxHeight: isFullScreen ? '100vh' : '90vh',
            height: isFullScreen ? '100vh' : 'auto',
          },
        }}
      >
        <DialogTitle id="scroll-dialog-title">
          <Typography variant="h5" component="span">
            {getModalTitle()}
          </Typography>
          {attribute?.data_type !== 'Document' && (
            <IconButton
              color="primary"
              size="small"
              aria-label="Expand dialog to fullscreen"
              onClick={toggleFullScreen}
              sx={{
                position: 'absolute',
                right: 48,
                top: 8,
              }}
            >
              {isFullScreen ? (
                <FullscreenExitIcon width={18} height={18} />
              ) : (
                <FullscreenIcon width={18} height={18} />
              )}
            </IconButton>
          )}
          <IconButton
            color="primary"
            size="small"
            aria-label="Close dialog"
            onClick={handleCancelChanges}
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
            }}
          >
            <CloseIcon width={18} height={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            ...(attribute?.input_control_type === 'MULTI_DOCUMENT' && {
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }),
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              flexGrow: 1,
              ...(attribute?.input_control_type === 'MULTI_DOCUMENT' && {
                minHeight: 0,
              }),
            }}
          >
            <Grid
              size={12}
              sx={{
                ...(attribute?.input_control_type === 'MULTI_DOCUMENT' && {
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }),
              }}
            >
              {renderFieldComponent()}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          {existingDocument && (
            <Button
              onClick={handleDownloadDocument}
              variant="outlined"
              title="Download"
            >
              Download
            </Button>
          )}

          {mode === 'view' ? (
            <Button onClick={handleClose} variant="contained" title="Close">
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={handleCancelChanges}
                variant="outlined"
                title="Cancel"
              >
                Cancel
              </Button>

              <Button
                /**
                 * as we are directly manipulating values inside formik form, so we no longer need this save button
                 * this button will just close the model
                 * Here we will only update tags of doc if the type of attr is Document.
                 */
                onClick={handleOnClose}
                variant="contained"
                title={saveButtonTitle}
                disabled={isDerivedExtended}
              >
                {saveButtonText}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog when user attempts to Save with Failed Uploads */}
      <Dialog
        open={failedDocsConfirmOpen}
        onClose={handleCancelFailedConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            pt: 2,
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: '1.05rem' }}
          >
            Some files failed to upload
          </Typography>
          <IconButton
            size="small"
            onClick={handleCancelFailedConfirm}
            title="Close"
            sx={{ color: 'text.secondary', p: 0.5 }}
          >
            <CloseIcon width={18} height={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {failedDocsList.length === 1
              ? '1 file failed to upload. Remove it and save the remaining documents?'
              : `${failedDocsList.length} files failed to upload. Remove them and save the remaining documents?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pt: 1.5, pb: 2, gap: 1.25 }}>
          <Button
            onClick={handleCancelFailedConfirm}
            variant="outlined"
            size="small"
          >
            Back to Edit
          </Button>
          <Button
            onClick={handleRemoveFailedAndSave}
            variant="contained"
            color="primary"
            size="small"
          >
            Remove Failed & Save
          </Button>
        </DialogActions>
      </Dialog>
    </DocumentCleanupProvider>
  );
};

MultiEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  handleSave: PropTypes.func.isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  mode: PropTypes.string,
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }),
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
    values: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
        PropTypes.object,
      ])
    ),
  }).isRequired,
  handleAutoSave: PropTypes.func,
  isDerivedExtended: PropTypes.bool.isRequired,
  configColumn: PropTypes.bool,
  modelTemplateDetails: PropTypes.shape({}),
  isUserAuthorizedToOverrideAttribute: PropTypes.bool,
};

export default MultiEditModal;
